import type { WebsiteContent } from './website';

export interface SearchTarget {
  id: string;
  term: string;
}

export type SearchDifficulty = 'strong' | 'realistic' | 'hard';

export interface SearchTargetAnalysis {
  target: SearchTarget;
  /** How much of the phrase the site's own words actually support, 0–100. */
  coverage: number;
  difficulty: SearchDifficulty;
  difficultyNote: string;
  /** Where the matching words were found. */
  found: string[];
  /** Words in the phrase that appear nowhere on the site. */
  missing: string[];
  advice: string;
}

export const searchTargetsStorageKey = 'artistos-search-targets';

export const defaultSearchTargets: SearchTarget[] = [
  { id: 'target-band-name', term: 'Cooper Ty Band' },
  { id: 'target-texas-country', term: 'Texas country band Fort Worth' },
  { id: 'target-shows', term: 'Cooper Ty tour dates' },
  { id: 'target-billy-bobs', term: 'Cooper Ty Band Billy Bob’s Texas' },
  { id: 'target-college-station', term: 'Cooper Ty College Station' },
];

const stopWords = new Set(['the', 'a', 'an', 'and', 'of', 'in', 'for', 'to', 'near', 'me', 'my', 'best', 'top']);

export function getSearchTargets(): SearchTarget[] {
  try {
    const stored = window.localStorage.getItem(searchTargetsStorageKey);
    if (!stored) return defaultSearchTargets;
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? (parsed as SearchTarget[]) : defaultSearchTargets;
  } catch {
    return defaultSearchTargets;
  }
}

export function saveSearchTargets(targets: SearchTarget[]): boolean {
  try {
    window.localStorage.setItem(searchTargetsStorageKey, JSON.stringify(targets));
    return true;
  } catch {
    return false;
  }
}

/** Every place on the site where words can be read by a search engine. */
function surfaces(content: WebsiteContent): { label: string; text: string }[] {
  return [
    { label: 'Page title', text: content.seoTitle },
    { label: 'Search description', text: content.seoDescription },
    { label: 'Band name', text: content.bandName },
    { label: 'Hero text', text: `${content.heroEyebrow} ${content.heroHeadline} ${content.heroDescription}` },
    {
      label: 'About page',
      text: content.aboutHidden
        ? ''
        : `${content.aboutTitle} ${content.aboutSeoTitle} ${content.aboutSeoDescription} ${content.aboutBody} ${content.aboutMembers
            .map((member) => `${member.name} ${member.role}`)
            .join(' ')}`,
    },
    {
      label: 'Venue & award credits',
      text: content.aboutHidden
        ? ''
        : [
            content.aboutVenues.map((venue) => `${venue.venue} ${venue.city}`).join(' '),
            content.aboutAwards.map((award) => award.title).join(' '),
            content.aboutSharedStage,
            content.aboutEndorsements.map((entry) => entry.artist).join(' '),
          ].join(' '),
    },
    { label: 'City & genre', text: `${content.seoLocation} ${content.seoGenre}` },
    {
      label: 'Tour dates',
      text: content.showsHidden ? '' : content.shows.map((show) => `${show.venue} ${show.city}`).join(' '),
    },
    {
      label: 'Photo captions',
      text: content.galleryHidden ? '' : content.galleryImages.map((image) => image.caption).join(' '),
    },
    { label: 'Music section', text: content.musicHidden ? '' : `${content.musicHeadline} ${content.musicDescription}` },
  ];
}

function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 1 && !stopWords.has(word));
}

function classify(term: string, content: WebsiteContent): { difficulty: SearchDifficulty; note: string; advice: string } {
  const lower = term.toLowerCase();
  const bandWords = words(content.bandName);
  const memberNames = content.aboutMembers.map((member) => member.name.toLowerCase()).filter(Boolean);
  const isBandName = bandWords.length > 0 && bandWords.every((word) => lower.includes(word));
  const isMemberName = memberNames.some((name) => name && lower.includes(name));

  if (isBandName || isMemberName) {
    return {
      difficulty: 'strong',
      note: 'You should win this',
      advice:
        'Searches for a name are yours to lose. Once Google has indexed the site, your own domain almost always ranks first for it.',
    };
  }

  // Broad category searches are dominated by venues, festivals, and press.
  if (words(term).length <= 2) {
    return {
      difficulty: 'hard',
      note: 'Very competitive',
      advice:
        'Short category searches are dominated by venues, festivals, and local publications with years of history. Aim for a longer, more specific version of this phrase instead.',
    };
  }

  return {
    difficulty: 'realistic',
    note: 'Winnable over time',
    advice:
      'Specific phrases like this are reachable. It needs the words on your page, plus links from venues and local press, and usually a few months.',
  };
}

export function analyzeSearchTarget(target: SearchTarget, content: WebsiteContent): SearchTargetAnalysis {
  const termWords = Array.from(new Set(words(target.term)));
  const available = surfaces(content).filter((surface) => surface.text.trim());

  const found: string[] = [];
  const matched = new Set<string>();

  available.forEach((surface) => {
    const surfaceWords = new Set(words(surface.text));
    const hits = termWords.filter((word) => surfaceWords.has(word));
    if (hits.length) {
      hits.forEach((hit) => matched.add(hit));
      // Only credit a surface that carries most of the phrase.
      if (hits.length >= Math.ceil(termWords.length * 0.6)) found.push(surface.label);
    }
  });

  const missing = termWords.filter((word) => !matched.has(word));
  const coverage = termWords.length ? Math.round((matched.size / termWords.length) * 100) : 0;
  const { difficulty, note, advice } = classify(target.term, content);

  return { target, coverage, difficulty, difficultyNote: note, found, missing, advice };
}
